// Pure logic for the page taskbar — the strip of tabs that replaced the
// "MadameFin Backoffice : <env>" title on the top navbar.
// Spec: docs/specs/2026-10-03-bo-taskbar-design.md §3-§5.
//
// Consumer: src/components/Taskbar/PageTaskbar.vue (mounted in
// src/views/Layout/DashboardNavbar.vue). Behaviour is pinned by
// tests/unit/taskbar.spec.js.
//
// WHY A VUE-FREE FILE
//
// This repo's jest cannot mount a .vue (see reference-bo-fn-jest-infra), so
// everything decidable about the strip lives here where a spec can call it:
// which tab a key belongs to, what a tab is called, which tab gets evicted
// when the cap is reached, where the user lands after closing the tab they are
// standing on, and what a corrupted localStorage payload is allowed to do.
// The component only renders and routes.
//
// THREE RULES THIS FILE EXISTS TO KEEP
//
//   1. NOTHING MUTATES. Every function returns a fresh state; the caller keeps
//      the old one. Vue 2 cannot see `state.tabs[i].pinned = x` on an object
//      it never observed, and the persisted copy must never diverge from the
//      rendered one.
//   2. A PINNED TAB IS NEVER EVICTED. Pinning is the user saying "I am coming
//      back to this", so the cap always takes the oldest UNPINNED tab first and
//      never anything else. This was briefly weakened on 2026-10-04 (ten pins
//      and nothing loose left had to drop a pin to show the new page); the pin
//      cap below restored it, because five pins out of ten tabs always leaves
//      something loose to take instead. See clampToMax.
//   3. RESTORE NEVER THROWS. The payload comes from localStorage, which the
//      user, an older build, or another tab can have written. A bad byte there
//      must cost an empty strip, not a white screen on every page of the BO.

import { currentHrefOf, currentPathOf, isLegacyPath } from "@/utils/legacyPaths";

// How many tabs the strip may hold IN TOTAL, pinned and unpinned together
// (owner's rule, 2026-10-04). Opening an eleventh evicts the oldest UNPINNED
// tab — see clampToMax.
export const TASKBAR_MAX_TABS = 10;

// How many of those ten may be pinned (owner, 2026-10-04). Half the strip is
// the most the operator may nail down; the rest has to stay free for the pages
// they are actually working through, or the tab they open next pushes out the
// one they opened a minute ago.
//
// This cap is also what makes rule 2 absolute again: with at most five pins in
// a strip of ten there is ALWAYS an unpinned tab for the cap to take, so a pin
// can never be evicted. The sixth pin is refused instead — a refusal the user
// can see (the pin control is not rendered) rather than a deletion they cannot.
export const TASKBAR_MAX_PINNED = 5;

// Kept as a published name (the blind suite imports it). The strip no longer
// counts the two halves separately — an unpinned tab cannot outnumber the total
// — so this is the same number, not a second budget.
export const TASKBAR_MAX_UNPINNED = TASKBAR_MAX_TABS;

const isNonEmptyString = (v) => typeof v === "string" && v !== "";

// "/seller/orders?tab=shipping#x" -> "/seller/orders". Exported because the
// component and the specs need the same answer for a bare string key.
// Pages whose QUERY is part of the tab's identity (owner 2026-10-05: "หน้า
// backoffice/orders เอาไว้แบบเดิม"). /backoffice/orders?status=141 (ตีกลับ)
// and ?status=143 (ห้องแพ็ค) are two screens the operator keeps open side by
// side, so they stay two tabs. Every other page keys by path only.
export const QUERY_KEYED_PATHS = ["/backoffice/orders"];

export function taskbarPathOf(value) {
	if (!isNonEmptyString(value)) return "";
	// A renamed page's old path keys as the new one (src/utils/legacyPaths.js,
	// #441), so a tab saved before the rename and the page it redirects to are
	// ONE tab — restore, open, merge and the server pins all key through here.
	const path = currentPathOf(value.split(/[?#]/)[0]);
	if (QUERY_KEYED_PATHS.includes(path)) return currentHrefOf(value.split("#")[0]);
	return path;
}

// Identity of a tab: the route PATH, never the query or hash.
//
// Changed 2026-10-05 (owner). It used to be fullPath, on the theory that
// /backoffice/orders?status=3 and /backoffice/orders are two screens; then
// /seller/orders started writing its status tabs into ?tab=… and the strip
// filled with five tabs all called "คำสั่งซื้อร้านค้า", burning half the cap.
// The query is still remembered — on the tab's `href` (taskbarHref), which is
// where clicking the tab goes — it just no longer makes a new tab.
export function taskbarKey(route) {
	if (!route || typeof route !== "object") return "";
	// fullPath first: for a QUERY_KEYED_PATHS page the query is the identity,
	// and taskbarPathOf strips it for every other page anyway.
	if (isNonEmptyString(route.fullPath)) return taskbarPathOf(route.fullPath);
	if (isNonEmptyString(route.path)) return taskbarPathOf(route.path);
	return "";
}

// Where clicking the tab for this route should go: the full path, query
// included, of the last time the page was visited.
export function taskbarHref(route) {
	if (!route || typeof route !== "object") return "";
	if (isNonEmptyString(route.fullPath)) return route.fullPath;
	if (isNonEmptyString(route.path)) return route.path;
	return "";
}

// The i18n key the breadcrumb would render for this route: the route's own
// meta.breadcrumb, else the deepest matched record that has one — the same
// walk src/components/Breadcrumb/RouteBreadcrumb.vue does over $route.matched.
function breadcrumbKey(route) {
	if (!route || typeof route !== "object") return "";
	if (route.meta && isNonEmptyString(route.meta.breadcrumb)) return route.meta.breadcrumb;
	const matched = Array.isArray(route.matched) ? route.matched : [];
	for (let i = matched.length - 1; i >= 0; i--) {
		const m = matched[i];
		if (m && m.meta && isNonEmptyString(m.meta.breadcrumb)) return m.meta.breadcrumb;
	}
	return "";
}

// Label shown on the tab. `t` is vue-i18n's $t (optional).
//
// The `translated !== key` test is not pedantry: vue-i18n returns the key
// itself when a path does not resolve, so without it a route whose
// meta.breadcrumb is stale would put a raw dotted path like
// "wms.menu.inventory_list" on the tab instead of falling back to a name a
// human wrote.
export function taskbarLabel(route, t) {
	const key = breadcrumbKey(route);
	if (key && typeof t === "function") {
		let translated;
		try {
			translated = t(key);
		} catch (e) {
			translated = undefined;
		}
		if (isNonEmptyString(translated) && translated !== key) return translated;
	}
	if (route && isNonEmptyString(route.name)) return route.name;
	return taskbarKey(route);
}

// `href` is copied only when the tab has one: a tab without it means "href =
// key" everywhere it is read (component go(), serialize), and inventing one
// here would make a no-op (a refused pin) look like a change.
const cloneTab = (tab) => {
	const copy = { key: tab.key, label: tab.label, pinned: tab.pinned, seq: tab.seq };
	if (isNonEmptyString(tab.href)) copy.href = tab.href;
	return copy;
};

// One entry per PATH. Used on everything that comes back from storage or the
// server, because a strip saved by the fullPath build (before 2026-10-05) can
// hold "/seller/orders?tab=shipping" and "/seller/orders?tab=completed" side
// by side.
//
//   - key   -> the path (query/hash stripped);
//   - href  -> the LAST occurrence's href (its own href, else its raw key):
//              entries are saved in strip order and the later one is the
//              newer visit, so that is the query the user expects back;
//   - place -> the FIRST occurrence's: the tab does not jump;
//   - label -> the first non-empty one (they are the same page);
//   - pinned-> pinned if ANY copy was: migration must never cost a pin;
//   - seq   -> the highest: the most recent visit decides eviction.
//
// Entries without a usable key are dropped. Never throws; never mutates.
export function dedupeByPath(list) {
	const out = [];
	const byKey = new Map();
	if (!Array.isArray(list)) return out;
	for (const raw of list) {
		if (!raw || typeof raw !== "object") continue;
		const key = taskbarPathOf(raw.key);
		if (!key) continue;
		const href = currentHrefOf(isNonEmptyString(raw.href) ? raw.href : raw.key);
		const seq = typeof raw.seq === "number" && isFinite(raw.seq) ? raw.seq : undefined;
		// A tab saved under a renamed path carries the page's OLD name: drop it.
		// taskbarRestore then falls back to the key, PageTaskbar relabels it, and
		// taskbarOpen renames it on the next visit anyway.
		const label = isLegacyPath(raw.key) ? undefined : raw.label;
		const found = byKey.get(key);
		if (!found) {
			const entry = { key, href, label, pinned: !!raw.pinned, seq };
			byKey.set(key, entry);
			out.push(entry);
			continue;
		}
		found.href = href;
		if (!isNonEmptyString(found.label) && isNonEmptyString(label)) found.label = label;
		if (raw.pinned) found.pinned = true;
		if (seq !== undefined && (found.seq === undefined || seq > found.seq)) found.seq = seq;
	}
	return out;
}

const tabsOf = (state) => (state && Array.isArray(state.tabs) ? state.tabs : []);

const nextSeq = (tabs) => tabs.reduce((max, t) => (t.seq > max ? t.seq : max), 0) + 1;

// pinned block first, each block keeping its own relative order.
const ordered = (tabs) => [...tabs.filter((t) => t.pinned), ...tabs.filter((t) => !t.pinned)];

// Trim a strip down to TASKBAR_MAX_TABS by dropping the oldest UNPINNED tab —
// a page the user merely passed through — and nothing else. `keepKey` is the
// tab the caller is opening right now and may never be the victim: evicting the
// page the user is arriving on would close the tab as it appears.
//
// A pinned tab is never a candidate. The 2026-10-04 version had a second branch
// that took the oldest pin once every slot was pinned; TASKBAR_MAX_PINNED makes
// that state unreachable (five pins can never fill ten slots), so the branch
// went rather than sitting there untested and untestable. The `break` below is
// the only thing left of it: if a future cap ever did make the strip all-pinned
// it leaves the strip one tab too wide instead of silently eating a pin.
//
// MUTATES the array it is given; every caller here owns a fresh copy.
function clampToMax(tabs, keepKey) {
	while (tabs.length > TASKBAR_MAX_TABS) {
		const pool = tabs.filter((tab) => !tab.pinned && tab.key !== keepKey);
		if (!pool.length) break;
		let victim = pool[0];
		for (const tab of pool) {
			if (tab.seq < victim.seq) victim = tab;
		}
		tabs.splice(tabs.indexOf(victim), 1);
	}
	return tabs;
}

// Keep at most TASKBAR_MAX_PINNED pins, the most recently touched ones, and
// DEMOTE the rest to ordinary tabs — never drop them. A strip saved before the
// pin cap existed, or by another browser tab running an older build, is the
// normal way to get here; losing a page because it was "too pinned" is not
// something the user could make sense of.
//
// MUTATES. Returns the same array.
function clampPins(tabs) {
	const pinned = tabs.filter((tab) => tab.pinned);
	if (pinned.length <= TASKBAR_MAX_PINNED) return tabs;
	// newest first by seq, then keep the first MAX as pins
	const keep = new Set(
		pinned
			.slice()
			.sort((a, b) => b.seq - a.seq)
			.slice(0, TASKBAR_MAX_PINNED)
			.map((tab) => tab.key)
	);
	for (const tab of tabs) {
		if (tab.pinned && !keep.has(tab.key)) tab.pinned = false;
	}
	return tabs;
}

// May the pin control be offered for this tab? Pure, and exported, so the
// template can ask without the component owning the rule — this repo's jest
// cannot mount a .vue, so a rule that lives in a `v-if` is a rule no spec can
// reach.
//
// Accepts a tabs array or a whole state. A tab that is already pinned always
// answers true: UNPINNING is never capped, and hiding its control would strand
// the user at five pins forever.
export function taskbarCanPin(tabs, key) {
	const list = Array.isArray(tabs) ? tabs : tabsOf(tabs);
	const target = list.find((tab) => tab && tab.key === key);
	if (!target) return false;
	if (target.pinned) return true;
	return list.filter((tab) => tab && tab.pinned).length < TASKBAR_MAX_PINNED;
}

// Open (or revisit) a route.
//
// A tab that already exists does NOT move. The instruction called this "MRU",
// and recency does decide eviction below — but reordering on revisit makes the
// strip rearrange itself under the cursor every time the user clicks a tab,
// and the next click lands on a different page than the one they aimed at.
// Position is therefore insertion order; `seq` carries the recency.
export function taskbarOpen(state, route, t) {
	const key = taskbarKey(route);
	const href = taskbarHref(route) || key;
	// No key = not a page we can route back to (the layout's own record, a
	// malformed route object). Leave the strip exactly as it was.
	if (!key) return state && Array.isArray(state.tabs) ? state : { tabs: [] };

	const tabs = tabsOf(state).map(cloneTab);
	const seq = nextSeq(tabs);
	const label = taskbarLabel(route, t) || key;
	const existing = tabs.find((tab) => tab.key === key);

	// Same page, another query (/seller/orders?tab=completed after ?tab=shipping):
	// the SAME tab, now pointing at where the user is. Position and pin stay.
	if (existing) {
		existing.href = href;
		existing.label = label;
		existing.seq = seq;
		return { tabs: ordered(tabs) };
	}

	tabs.push({ key, href, label, pinned: false, seq });

	// Ten tabs, total. See clampToMax for which one goes.
	clampToMax(tabs, key);

	return { tabs: ordered(tabs) };
}

// Close one tab. Returns the new state AND where to go if the closed tab was
// the active one: the neighbour to the right, else the one to the left, else
// null (caller falls back to the dashboard). Right first because that is what
// every browser does, and because the tab to the left of a just-opened tab is
// usually the one the user came from and already finished with.
export function taskbarClose(state, key) {
	const tabs = tabsOf(state).map(cloneTab);
	const index = tabs.findIndex((tab) => tab.key === key);
	if (index === -1) return { state: { tabs }, nextKey: null };

	tabs.splice(index, 1);
	let nextKey = null;
	if (tabs.length) nextKey = (tabs[index] || tabs[index - 1] || tabs[tabs.length - 1]).key;
	return { state: { tabs }, nextKey };
}

// Pin / unpin. Pinning moves the tab to the end of the pinned block, unpinning
// to the end of the unpinned block (rightmost = most recent).
//
// Unpinning deliberately does not evict, even when it pushes the unpinned
// count over the cap: the user asked to keep that page, and answering by
// deleting one of their other pages is not something they can undo.
export function taskbarTogglePin(state, key) {
	const tabs = tabsOf(state).map(cloneTab);
	const target = tabs.find((tab) => tab.key === key);
	if (!target) return { tabs };
	// The sixth pin is refused, not swapped for an older one: the user asked to
	// ADD a pin, and quietly dropping one they made earlier is a different
	// answer to a different question. The control is not rendered in that state
	// (taskbarCanPin), so this is the belt to that braces.
	if (!taskbarCanPin(tabs, key)) return { tabs };

	target.pinned = !target.pinned;
	const others = tabs.filter((tab) => tab.key !== key);
	const pinned = others.filter((tab) => tab.pinned);
	const loose = others.filter((tab) => !tab.pinned);
	return { tabs: target.pinned ? [...pinned, target, ...loose] : [...pinned, ...loose, target] };
}

export function taskbarSerialize(state) {
	const tabs = tabsOf(state).map((tab) => ({
		key: tab.key,
		href: isNonEmptyString(tab.href) ? tab.href : tab.key,
		label: tab.label,
		pinned: !!tab.pinned,
		seq: typeof tab.seq === "number" && isFinite(tab.seq) ? tab.seq : 0
	}));
	return JSON.stringify({ v: 1, tabs });
}

// Rebuild a state from whatever is in storage. Every failure mode lands on
// `{ tabs: [] }` rather than an exception — see rule 3 at the top.
export function taskbarRestore(json) {
	let parsed = json;
	if (typeof json === "string") {
		try {
			parsed = JSON.parse(json);
		} catch (e) {
			return { tabs: [] };
		}
	}
	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return { tabs: [] };
	if (!Array.isArray(parsed.tabs)) return { tabs: [] };

	// A missing/bad seq falls back to the entry's index in the payload, as it
	// always has; stamped BEFORE the dedupe so the index is the stored one.
	const stamped = parsed.tabs.map((raw, index) =>
		raw && typeof raw === "object" ? { ...raw, seq: typeof raw.seq === "number" && isFinite(raw.seq) ? raw.seq : index } : raw
	);
	const tabs = dedupeByPath(stamped).map((tab) => ({
		key: tab.key,
		href: tab.href,
		label: isNonEmptyString(tab.label) ? tab.label : tab.key,
		pinned: tab.pinned,
		seq: tab.seq
	}));

	// A payload wider than the cap — an older build that allowed twelve, or more
	// than five pins, hand-edited storage, two tabs of the BO saving over each
	// other — is brought into line HERE rather than on the next open, so the
	// strip never renders wider than it is allowed to be even once.
	//
	// Pins first, then the total: demoting an extra pin turns it into a
	// candidate for the width trim, which is the right order. A tab is never
	// dropped for being pinned, only for being the oldest loose one.
	return { tabs: ordered(clampToMax(clampPins(tabs), null)) };
}

// Is this route a page the taskbar should hold? The login / forgot-password /
// LINE-callback screens are not: they render in AuthLayout, outside this
// component, and the only reason one ever reached the strip is that the $route
// watcher fires once during the teardown tick when the user logs out. A
// "เข้าสู่ระบบ" tab is then the first thing they see on the way back in.
//
// The test is "some matched record asks for auth OR some matched record names a
// breadcrumb", not just the first half: 144 of the 152 route records in
// routes.js carry requiresAuth and the other eight are PARENT menu records that
// carry only a breadcrumb, so a leaf that someone forgets to guard still keeps
// its tab. The auth screens have neither.
export function taskbarIsPage(route) {
	if (!route || typeof route !== "object") return false;
	const records = Array.isArray(route.matched) && route.matched.length ? route.matched : [route];
	return records.some((r) => {
		const meta = (r && r.meta) || {};
		return meta.requiresAuth === true || isNonEmptyString(meta.breadcrumb);
	});
}

// Merge the pinned state with the loose one. Both now live in localStorage —
// the owner's call on 2026-10-04: EVERY tab has to survive a logout, and
// sessionStorage cannot promise that. A key present in both is the pinned one:
// the pinned copy is the one the user asked us to remember.
//
// Both halves are deduped by PATH first (dedupeByPath). A path in both halves
// keeps the pinned copy's place, pin and label, but takes the loose copy's
// href: the loose half is the whole strip as last written, so its href is the
// newer visit (and the server's pins carry no query at all).
export function taskbarMerge(pinnedState, sessionState) {
	const pinned = dedupeByPath(tabsOf(pinnedState)).map((tab) => ({ ...cloneTab(tab), pinned: true }));
	const byKey = new Map(pinned.map((tab) => [tab.key, tab]));
	const loose = [];
	for (const tab of dedupeByPath(tabsOf(sessionState))) {
		const twin = byKey.get(tab.key);
		if (twin) {
			twin.href = tab.href;
			continue;
		}
		byKey.set(tab.key, tab);
		loose.push({ ...cloneTab(tab), pinned: !!tab.pinned });
	}
	// Clamped for the same reason restore is: the two halves are read from two
	// keys that can each be at the limit on their own, and everything in the
	// pinned half arrives pinned.
	return { tabs: ordered(clampToMax(clampPins([...pinned, ...loose]), null)) };
}

// ---------------------------------------------------------------------------
// Server-side pins (spec docs/specs/2026-10-05-bo-taskbar-server-pins-design.md §4)
//
// The PINNED tabs also live in bo_user_taskbar (GET/PUT /api/v1/profile/taskbar)
// so they follow the staff member to another browser. Loose tabs never leave
// localStorage. The three functions below are additions only — nothing above
// changed behaviour.
// ---------------------------------------------------------------------------

// Limits the API enforces (mdf-bo-api controllers/BO-UserTaskbar.go). A pin the
// server would reject is left OUT of the payload rather than sent: one bad pin
// would otherwise 400 the whole set and nothing would sync at all. Such a pin
// stays pinned locally — it just does not travel.
export const TASKBAR_SERVER_KEY_MAX_BYTES = 512;
export const TASKBAR_SERVER_LABEL_MAX_CHARS = 200;

// UTF-8 byte length without TextEncoder (jsdom does not always provide it).
// A lone surrogate counts as the 3 bytes of U+FFFD it would be encoded as.
function utf8Bytes(s) {
	let n = 0;
	for (const ch of s) {
		const cp = ch.codePointAt(0);
		if (cp < 0x80) n += 1;
		else if (cp < 0x800) n += 2;
		else if (cp < 0x10000) n += 3;
		else n += 4;
	}
	return n;
}

// The body of PUT /api/v1/profile/taskbar's `pinned`: the pinned tabs in strip
// order, at most TASKBAR_MAX_PINNED, each one shaped {key,label,seq} the way the
// server validates it (key starts with "/", <= 512 bytes, unique; label <= 200
// characters; seq a number >= 0). Never throws.
export function taskbarPinsPayload(state) {
	const out = [];
	const seen = new Set();
	for (const tab of tabsOf(state)) {
		if (out.length >= TASKBAR_MAX_PINNED) break;
		if (!tab || typeof tab !== "object" || !tab.pinned) continue;
		// Pins travel keyed by PATH (2026-10-05): a query variant of a pinned
		// page is the same pin, and the signature must not move every time the
		// user switches a status tab on it. The query stays in localStorage.
		const key = taskbarPathOf(tab.key);
		if (!isNonEmptyString(key) || key.charAt(0) !== "/") continue;
		if (utf8Bytes(key) > TASKBAR_SERVER_KEY_MAX_BYTES) continue;
		if (seen.has(key)) continue;
		seen.add(key);
		const label = typeof tab.label === "string" ? Array.from(tab.label).slice(0, TASKBAR_SERVER_LABEL_MAX_CHARS).join("") : "";
		const seq = typeof tab.seq === "number" && isFinite(tab.seq) && tab.seq >= 0 ? tab.seq : 0;
		out.push({ key, label, seq });
	}
	return out;
}

// "Did the pin set change since we last agreed with the server?" — the keys of
// the payload, in order, joined by "\n". ORDER-SENSITIVE on purpose: the strip
// order of the pins is part of what syncs (acceptance §6.3). Labels and seq are
// left out: a label differs per UI language and seq moves on every visit, and
// neither is a reason to write. Built from the payload so a pin that cannot
// travel (key too long) never makes the signature differ forever.
export function taskbarPinsSignature(state) {
	return taskbarPinsPayload(state)
		.map((pin) => pin.key)
		.join("\n");
}

// Adopt the server's pin set: SERVER WINS. The server's pins become this
// strip's pins, in the server's order; every local tab that is not one of them
// stays on the strip as an ordinary tab — a local pin the server does not know
// is DEMOTED, never deleted (same philosophy as clampPins).
//
// `relabel(key)` (optional) returns the label for this machine's language, or
// a falsy value to keep the server's (the language of the machine that pinned).
//
// Former local pins are given the newest seqs among the loose tabs so that,
// if the merged strip is wider than TASKBAR_MAX_TABS, the width trim takes
// pages the user merely passed through before it takes a page they had pinned.
//
// Never throws — `serverPinned` is network input (rule 3).
export function taskbarAdoptServerPins(localState, serverPinned, relabel) {
	const rawPins = Array.isArray(serverPinned) ? serverPinned : [];
	const serverTabs = [];
	rawPins.forEach((pin) => {
		if (!pin || typeof pin !== "object") return;
		let label = pin.label;
		if (typeof relabel === "function" && isNonEmptyString(pin.key)) {
			try {
				const fresh = relabel(pin.key);
				if (isNonEmptyString(fresh)) label = fresh;
			} catch (e) {
				/* keep the server's label */
			}
		}
		// Renamed page: key it by its new path here, so the label just resolved
		// above survives (dedupeByPath drops a legacy key's saved label).
		serverTabs.push({ key: currentHrefOf(pin.key), label, seq: pin.seq, pinned: true });
	});
	const server = taskbarRestore({ tabs: serverTabs });

	const local = dedupeByPath(tabsOf(localState));
	let top = local.reduce((max, t) => (typeof t.seq === "number" && isFinite(t.seq) && t.seq > max ? t.seq : max), 0);
	const demoted = local.map((tab) => {
		const copy = { ...cloneTab(tab), pinned: false };
		if (tab.pinned) copy.seq = ++top;
		return copy;
	});

	return taskbarMerge(server, { tabs: demoted });
}

// ---------------------------------------------------------------------------
// Scroll buttons (docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md
// §2.1 A9, owner round 2: "กรณีแถบล้นหน้าให้ทำปุ่มเลื่อนซ้ายขวาให้ด้วย").
//
// The strip's scrollbar is hidden, so these two decide everything the user
// can see about overflow. Vue-free and DOM-free: the component passes in the
// three numbers it read off the strip element.
//
// A 1px tolerance on the overflow test and the right end: browsers report
// fractional scroll positions on zoomed / high-DPI screens (scrollLeft 299.5
// against a max of 300), and without slack the "right" button would stay
// enabled at the end of the strip and do nothing when pressed — or the
// buttons would appear for a strip that overflows by a rounding error.
const SCROLL_TOLERANCE_PX = 1;
const SCROLL_STEP_MIN_PX = 80;
const SCROLL_STEP_RATIO = 0.6;

const finiteOr = (v, fallback) => (typeof v === "number" && isFinite(v) ? v : fallback);

// {scrollLeft, clientWidth, scrollWidth} -> {overflow, canLeft, canRight}.
// Anything unreadable (no element yet, jsdom's zeros, NaN) is "no overflow":
// hiding the buttons is the safe failure, a dead button is not.
export function taskbarScrollState(metrics) {
	const m = metrics && typeof metrics === "object" ? metrics : {};
	const scrollLeft = Math.max(0, finiteOr(m.scrollLeft, 0));
	const clientWidth = finiteOr(m.clientWidth, NaN);
	const scrollWidth = finiteOr(m.scrollWidth, NaN);
	const none = { overflow: false, canLeft: false, canRight: false };
	if (!(clientWidth >= 0) || !(scrollWidth >= 0)) return none;
	if (!(scrollWidth > clientWidth + SCROLL_TOLERANCE_PX)) return none;
	return {
		overflow: true,
		// Left end per spec: disabled at scrollLeft <= 0 (the start is always an
		// exact 0 — scrolling cannot overshoot it).
		canLeft: scrollLeft > 0,
		canRight: scrollLeft + clientWidth < scrollWidth - SCROLL_TOLERANCE_PX
	};
}

// How far one press of a scroll button moves the strip: 60% of what is
// visible, so the tab that was at the edge is still on screen after the jump
// and the user keeps their bearings; never less than 80px so a narrow strip
// still visibly moves.
export function taskbarScrollStep(clientWidth) {
	const w = finiteOr(clientWidth, 0);
	return Math.max(SCROLL_STEP_MIN_PX, Math.round(w * SCROLL_STEP_RATIO));
}

// How many px to move the strip's scrollLeft so the active tab is fully on
// screen: negative = scroll left, positive = right, 0 = already visible. Both
// arguments are getBoundingClientRect()-shaped ({left, right}). A tab wider
// than the strip lines its LEFT edge up (the label starts there).
//
// Why not the DOM's scroll-into-view API (inline/block "nearest"): it
// scrolls EVERY scrollable ancestor, the window included — on a page scrolled
// down past the (non-fixed) navbar, switching tab would yank the whole page
// back to the top just to show the strip. Moving the strip's own scrollLeft
// by this delta touches nothing else.
export function taskbarRevealDelta(stripRect, tabRect) {
	const num = (r, k) => (r && typeof r[k] === "number" && isFinite(r[k]) ? r[k] : NaN);
	const sl = num(stripRect, "left");
	const sr = num(stripRect, "right");
	const tl = num(tabRect, "left");
	const tr = num(tabRect, "right");
	if ([sl, sr, tl, tr].some((v) => isNaN(v))) return 0;
	if (tl < sl) return Math.round(tl - sl);
	if (tr > sr) {
		const toRight = tr - sr;
		const toLeft = tl - sl;
		return Math.round(Math.min(toRight, toLeft));
	}
	return 0;
}
